import { Component, OnDestroy, OnInit, ViewChild, inject, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { ColorPickerModule } from 'primeng/colorpicker';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { CurrentContextService } from '../../core/context/current-context.service';
import { LiveUpdateService } from '../../core/live-updates/live-update.service';
import { LocalizationService } from '../../core/localization/localization.service';
import { CultureSelectorComponent } from '../../shared/design-system/culture-selector.component';
import { CurrencySelectorComponent } from '../../shared/design-system/currency-selector.component';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { StructuredJsonFieldDef } from '../../shared/design-system/structured-json-form.model';
import { StructuredJsonFormComponent } from '../../shared/design-system/structured-json-form.component';
import {
  mergeStructuredJsonPayload,
  parseStructuredJsonPayload,
  readStructuredJsonBoolean,
  structuredJsonItemFromPayload
} from '../../shared/design-system/structured-json-form.utils';
import { CatalogService, MarketplaceStore, StoreBranding, StoreDomainBinding, StorefrontContentProfile } from './catalog.service';
import { StorefrontProfileEditorComponent } from './storefront-profile-editor.component';
import {
  MARKETPLACE_LIST_TOOLBAR_STYLES,
  MarketplaceLiveSubscription,
  compareSortValues,
  downloadCsv,
  liveStatusKey,
  subscribeMarketplaceOperationsLiveUpdates,
  toggleSortField,
  unsubscribeMarketplaceOperationsLiveUpdates
} from './marketplace-list-page.utils';

const STOREFRONT_NAV_ITEM_KEYS = [
  'Home', 'Products', 'Solutions', 'Articles', 'Assessments',
  'About', 'Faq', 'Contact', 'BookDemo', 'ScheduleMeeting'
] as const;

type StoreSortField = 'storeKey' | 'name' | 'status' | 'defaultCulture' | 'defaultCurrency';
type BrandingColorField =
  | 'primaryColor'
  | 'secondaryColor'
  | 'accentColor'
  | 'storefrontLinkColor'
  | 'backgroundColor'
  | 'cardColor'
  | 'borderColor'
  | 'mutedTextColor'
  | 'surfaceSoftColor'
  | 'highlightColor';

interface BrandingColorFieldConfig {
  field: BrandingColorField;
  labelKey: string;
  placeholder: string;
  pickerDefault: string;
}

const BRANDING_COLOR_FIELDS: BrandingColorFieldConfig[] = [
  { field: 'primaryColor', labelKey: 'catalog.storefront.branding.primaryColor', placeholder: '#64748B', pickerDefault: '#64748b' },
  { field: 'secondaryColor', labelKey: 'catalog.storefront.branding.secondaryColor', placeholder: '#94A3B8', pickerDefault: '#94a3b8' },
  { field: 'accentColor', labelKey: 'catalog.storefront.branding.accentColor', placeholder: '#0EA5E9', pickerDefault: '#0ea5e9' },
  { field: 'storefrontLinkColor', labelKey: 'catalog.storefront.branding.storefrontLinkColor', placeholder: '#3B82C4', pickerDefault: '#3b82c4' },
  { field: 'backgroundColor', labelKey: 'catalog.storefront.branding.backgroundColor', placeholder: '#F0F0F0', pickerDefault: '#f0f0f0' },
  { field: 'cardColor', labelKey: 'catalog.storefront.branding.cardColor', placeholder: '#FFFFFF', pickerDefault: '#ffffff' },
  { field: 'borderColor', labelKey: 'catalog.storefront.branding.borderColor', placeholder: '#E2E8F0', pickerDefault: '#e2e8f0' },
  { field: 'mutedTextColor', labelKey: 'catalog.storefront.branding.mutedTextColor', placeholder: '#64748B', pickerDefault: '#64748b' },
  { field: 'surfaceSoftColor', labelKey: 'catalog.storefront.branding.surfaceSoftColor', placeholder: '#F8F9FB', pickerDefault: '#f8f9fb' },
  { field: 'highlightColor', labelKey: 'catalog.storefront.branding.highlightColor', placeholder: '#FFF4E5', pickerDefault: '#fff4e5' }
];

@Component({
  standalone: true,
  selector: 'ain-storefront-store-manager',
  imports: [
    AinPageComponent,
    AinStateComponent,
    CultureSelectorComponent,
    CurrencySelectorComponent,
    InfoHintComponent,
    StructuredJsonFormComponent,
    TableModule,
    ButtonModule,
    DialogModule,
    FormsModule,
    InputTextModule,
    ColorPickerModule,
    StorefrontProfileEditorComponent
  ],
  template: `
    <ain-page [title]="t('catalog.storefront.stores.title')" [description]="t('catalog.storefront.stores.description')">
      <ain-info-hint
        page-title-addon
        [titleKey]="'catalog.marketplace.help.list.title'"
        [descriptionKey]="'catalog.marketplace.help.list.description'"
        [bulletKeys]="[
          'catalog.marketplace.help.list.bullets.selection',
          'catalog.marketplace.help.list.bullets.filters',
          'catalog.marketplace.help.list.bullets.live'
        ]" />
      @if (loading()) {
        <ain-state type="loading" [title]="t('catalog.loading')" />
      } @else {
        <section class="panel">
          <div class="toolbar">
            <button pButton type="button" [label]="t('catalog.storefront.stores.create')" (click)="openCreate()"></button>
            <button pButton type="button" severity="secondary" [label]="t('catalog.storefront.profile.tenantDefault.action')" (click)="showTenantDefaultProfile()"></button>
            <button pButton type="button" severity="secondary" [label]="t('catalog.marketplace.actions.refresh')" (click)="load()"></button>
            <button pButton type="button" severity="secondary" [label]="t('catalog.marketplace.actions.bulk')" (click)="applyBulk()"></button>
            <button pButton type="button" severity="secondary" [label]="t('catalog.marketplace.actions.export')" (click)="exportRows()"></button>
            <button pButton type="button" severity="secondary" [label]="t('catalog.marketplace.actions.open')" [disabled]="selectedRows().length !== 1" (click)="openSelected()"></button>
            <span class="live-status" [class]="liveUpdateService.connectionState()">
              {{ t(liveStatusKey(liveUpdateService.connectionState())) }}
            </span>
          </div>

          <details class="filters" [open]="true">
            <summary>
              <span>{{ t('catalog.marketplace.filters.title') }}</span>
              <ain-info-hint
                [titleKey]="'catalog.marketplace.help.filters.title'"
                [descriptionKey]="'catalog.marketplace.help.filters.description'"
                [bulletKeys]="[
                  'catalog.marketplace.help.filters.bullets.search',
                  'catalog.marketplace.help.filters.bullets.status'
                ]" />
            </summary>
            <div class="filter-grid">
              <label>
                <span>{{ t('catalog.marketplace.filters.search') }}</span>
                <input pInputText [value]="search()" (input)="onSearch($event)" />
              </label>
            </div>
          </details>

          @if (filteredRows().length === 0) {
            <ain-state type="empty" [title]="t('catalog.storefront.empty')" />
          } @else {
            <p-table
              [value]="filteredRows()"
              dataKey="id"
              [paginator]="true"
              [rows]="10"
              [selection]="selectedRows()"
              (selectionChange)="onSelectionChange($event)">
              <ng-template pTemplate="header">
                <tr>
                  <th style="width:44px"><p-tableHeaderCheckbox /></th>
                  <th>
                    <button type="button" class="sort-btn" [class.active]="sortField() === 'storeKey'" (click)="sortBy('storeKey')">
                      {{ t('catalog.storefront.stores.storeKey') }}
                    </button>
                  </th>
                  <th>
                    <button type="button" class="sort-btn" [class.active]="sortField() === 'name'" (click)="sortBy('name')">
                      {{ t('catalog.storefront.stores.name') }}
                    </button>
                  </th>
                  <th>
                    <button type="button" class="sort-btn" [class.active]="sortField() === 'status'" (click)="sortBy('status')">
                      {{ t('catalog.storefront.stores.status') }}
                    </button>
                  </th>
                  <th>
                    <button type="button" class="sort-btn" [class.active]="sortField() === 'defaultCulture'" (click)="sortBy('defaultCulture')">
                      {{ t('catalog.storefront.stores.culture') }}
                    </button>
                  </th>
                  <th>
                    <button type="button" class="sort-btn" [class.active]="sortField() === 'defaultCurrency'" (click)="sortBy('defaultCurrency')">
                      {{ t('catalog.storefront.stores.currency') }}
                    </button>
                  </th>
                  <th></th>
                </tr>
              </ng-template>
              <ng-template pTemplate="body" let-row>
                <tr>
                  <td><p-tableCheckbox [value]="row" /></td>
                  <td>{{ row.storeKey }}</td>
                  <td>{{ row.name }}</td>
                  <td>{{ row.status }}</td>
                  <td>{{ row.defaultCulture }}</td>
                  <td>{{ row.defaultCurrency }}</td>
                  <td>
                    <button pButton type="button" size="small" severity="secondary" [label]="t('catalog.storefront.stores.domains')" (click)="showDomains(row)"></button>
                    <button pButton type="button" size="small" severity="secondary" [label]="t('catalog.storefront.stores.settings')" (click)="showSettings(row)"></button>
                    <button pButton type="button" size="small" severity="secondary" [label]="t('catalog.storefront.branding.action')" (click)="showBranding(row)"></button>
                    <button pButton type="button" size="small" severity="secondary" [label]="t('catalog.storefront.profile.action')" (click)="showProfile(row)"></button>
                  </td>
                </tr>
              </ng-template>
            </p-table>
          }

          @if (info()) { <p class="info">{{ info() }}</p> }
        </section>
      }

      <p-dialog [header]="t('catalog.storefront.stores.create')" [(visible)]="storeDialogVisible" [modal]="true" [style]="{ width: '500px' }">
        <div class="form">
          <label>{{ t('catalog.storefront.stores.storeKey') }}</label><input pInputText [(ngModel)]="storeKey" class="full" />
          <label>{{ t('catalog.storefront.stores.name') }}</label><input pInputText [(ngModel)]="storeName" class="full" />
          <label>{{ t('catalog.storefront.stores.culture') }}</label><ain-culture-selector [(ngModel)]="defaultCulture" />
          <label>{{ t('catalog.storefront.stores.currency') }}</label><ain-currency-selector [(ngModel)]="defaultCurrency" />
          <label>{{ t('catalog.storefront.stores.flags') }}</label>
          <ain-structured-json-form
            [fields]="featureFlagFields"
            [payloadJson]="featureFlagsJson"
            (payloadJsonChange)="onFeatureFlagsJsonChange($event)" />
        </div>
        <ng-template pTemplate="footer">
          <button pButton type="button" severity="secondary" [label]="t('common.cancel')" (click)="storeDialogVisible = false"></button>
          <button pButton type="button" [label]="t('catalog.action.create')" [disabled]="acting()" (click)="saveStore()"></button>
        </ng-template>
      </p-dialog>

      <p-dialog [header]="t('catalog.storefront.stores.settings')" [(visible)]="settingsDialogVisible" [modal]="true" [style]="{ width: '520px' }">
        <ain-info-hint
          [titleKey]="'catalog.storefront.stores.packageModeHelp.title'"
          [descriptionKey]="'catalog.storefront.stores.packageModeHelp.description'"
          [bulletKeys]="[
            'catalog.storefront.stores.packageModeHelp.bullets.home',
            'catalog.storefront.stores.packageModeHelp.bullets.capabilities'
          ]" />
        <ain-info-hint
          [titleKey]="'catalog.storefront.stores.marketplaceModeHelp.title'"
          [descriptionKey]="'catalog.storefront.stores.marketplaceModeHelp.description'"
          [bulletKeys]="[
            'catalog.storefront.stores.marketplaceModeHelp.bullets.buybox',
            'catalog.storefront.stores.marketplaceModeHelp.bullets.home'
          ]" />
        <ain-info-hint
          [titleKey]="'catalog.storefront.stores.heroCarouselHelp.title'"
          [descriptionKey]="'catalog.storefront.stores.heroCarouselHelp.description'"
          [bulletKeys]="[
            'catalog.storefront.stores.heroCarouselHelp.bullets.layout',
            'catalog.storefront.stores.heroCarouselHelp.bullets.transition',
            'catalog.storefront.stores.heroCarouselHelp.bullets.json'
          ]" />
        <div class="form">
          <label>{{ t('catalog.storefront.stores.flags') }}</label>
          <ain-structured-json-form
            [fields]="featureFlagFields"
            [payloadJson]="featureFlagsJson"
            (payloadJsonChange)="onFeatureFlagsJsonChange($event)" />
        </div>
        <ng-template pTemplate="footer">
          <button pButton type="button" severity="secondary" [label]="t('common.cancel')" (click)="settingsDialogVisible = false"></button>
          <button pButton type="button" [label]="t('common.save')" [disabled]="acting() || !selectedStoreId()" (click)="saveSettings()"></button>
        </ng-template>
      </p-dialog>

      <p-dialog [header]="t('catalog.storefront.stores.domains')" [(visible)]="domainDialogVisible" [modal]="true" [style]="{ width: '560px' }">
        <div class="form">
          <label>{{ t('catalog.storefront.stores.host') }}</label><input pInputText [(ngModel)]="host" class="full" />
          <label>{{ t('catalog.storefront.stores.pathBase') }}</label><input pInputText [(ngModel)]="pathBase" class="full" />
          <button pButton type="button" [label]="t('catalog.action.create')" [disabled]="acting() || !selectedStoreId()" (click)="saveDomain()"></button>
          <p-table [value]="domains()">
            <ng-template pTemplate="header">
              <tr>
                <th>{{ t('catalog.storefront.stores.host') }}</th>
                <th>{{ t('catalog.storefront.stores.pathBase') }}</th>
                <th>{{ t('catalog.storefront.stores.primary') }}</th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-row>
              <tr>
                <td>{{ row.host }}</td>
                <td>{{ row.pathBase }}</td>
                <td>{{ row.isPrimary ? t('common.yes') : t('common.no') }}</td>
              </tr>
            </ng-template>
          </p-table>
        </div>
      </p-dialog>

      <p-dialog [header]="t('catalog.storefront.branding.title')" [(visible)]="brandingDialogVisible" [modal]="true" [style]="{ width: '620px' }">
        <ain-info-hint
          [titleKey]="'catalog.storefront.branding.help.title'"
          [descriptionKey]="'catalog.storefront.branding.help.description'"
          [bulletKeys]="[
            'catalog.storefront.branding.help.bullets.colors',
            'catalog.storefront.branding.help.bullets.assets',
            'catalog.storefront.branding.help.bullets.fallback'
          ]" />
        <div class="form">
          @for (colorField of brandingColorFields; track colorField.field) {
            <label>{{ t(colorField.labelKey) }}</label>
            <div class="color-row">
              <p-colorPicker
                [ngModel]="toPickerHex(brandingColors[colorField.field])"
                format="hex"
                [defaultColor]="colorField.pickerDefault"
                appendTo="body"
                (ngModelChange)="onColorPickerChange(colorField.field, $event)" />
              <input
                pInputText
                class="color-hex"
                [ngModel]="brandingColors[colorField.field]"
                [attr.aria-label]="t(colorField.labelKey)"
                [placeholder]="colorField.placeholder"
                (ngModelChange)="onColorHexChange(colorField.field, $event)" />
              <span
                class="color-swatch"
                [class.color-swatch--empty]="!brandingColors[colorField.field]"
                [style.background]="brandingColors[colorField.field] || 'transparent'"
                [attr.aria-label]="t(colorField.labelKey)"></span>
              @if (brandingColors[colorField.field]) {
                <button
                  pButton
                  type="button"
                  severity="secondary"
                  size="small"
                  icon="pi pi-times"
                  [attr.aria-label]="t('common.remove')"
                  (click)="clearBrandingColor(colorField.field)"></button>
              }
            </div>
          }

          <label>{{ t('catalog.storefront.branding.logo') }}</label>
          <div class="asset-row">
            <input type="file" accept="image/*" (change)="onLogoSelected($event)" />
            @if (logoPreviewUrl()) {
              <img class="asset-preview" [src]="logoPreviewUrl()!" [alt]="t('catalog.storefront.branding.logo')" />
            }
          </div>

          <label>{{ t('catalog.storefront.branding.favicon') }}</label>
          <div class="asset-row">
            <input type="file" accept="image/*,.ico" (change)="onFaviconSelected($event)" />
            @if (faviconPreviewUrl()) {
              <img class="asset-preview asset-preview--icon" [src]="faviconPreviewUrl()!" [alt]="t('catalog.storefront.branding.favicon')" />
            }
          </div>

          <p class="source-hint">{{ t('catalog.storefront.branding.source') }}: {{ brandingSource() }}</p>
        </div>
        <ng-template pTemplate="footer">
          <button pButton type="button" severity="secondary" [label]="t('common.cancel')" (click)="brandingDialogVisible = false"></button>
          <button pButton type="button" [label]="t('common.save')" [disabled]="acting() || !selectedStoreId()" (click)="saveBranding()"></button>
        </ng-template>
      </p-dialog>

      <p-dialog [header]="t('catalog.storefront.profile.title')" [(visible)]="profileDialogVisible" [modal]="true" [style]="{ width: '760px' }">
        @if (selectedStoreId()) {
          <ain-storefront-profile-editor
            #profileEditor
            editorMode="store"
            [storeId]="selectedStoreId()!"
            [source]="profileSource()"
            (profileChange)="onProfileDraftChange($event)" />
        }
        <ng-template pTemplate="footer">
          <button pButton type="button" severity="secondary" [label]="t('common.cancel')" (click)="profileDialogVisible = false"></button>
          <button pButton type="button" [label]="t('common.save')" [disabled]="acting() || !selectedStoreId()" (click)="saveProfile()"></button>
        </ng-template>
      </p-dialog>

      <p-dialog [header]="t('catalog.storefront.profile.tenantDefault.title')" [(visible)]="tenantProfileDialogVisible" [modal]="true" [style]="{ width: '760px' }">
        <ain-storefront-profile-editor
          #tenantProfileEditor
          editorMode="tenant-default"
          [source]="tenantProfileSource()"
          (profileChange)="onTenantProfileDraftChange($event)" />
        <ng-template pTemplate="footer">
          <button pButton type="button" severity="secondary" [label]="t('common.cancel')" (click)="tenantProfileDialogVisible = false"></button>
          <button pButton type="button" [label]="t('common.save')" [disabled]="acting()" (click)="saveTenantDefaultProfile()"></button>
        </ng-template>
      </p-dialog>
    </ain-page>
  `,
  styles: [MARKETPLACE_LIST_TOOLBAR_STYLES, `
    .color-row { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; margin-bottom: 4px; }
    .color-hex { flex: 1; min-width: 120px; max-width: 140px; font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace; }
    .color-swatch {
      width: 28px;
      height: 28px;
      border-radius: 6px;
      border: 1px solid #cbd5e1;
    }
    .color-swatch--empty {
      background:
        linear-gradient(45deg, #f8fafc 25%, transparent 25%),
        linear-gradient(-45deg, #f8fafc 25%, transparent 25%),
        linear-gradient(45deg, transparent 75%, #f8fafc 75%),
        linear-gradient(-45deg, transparent 75%, #f8fafc 75%) !important;
      background-size: 8px 8px !important;
      background-position: 0 0, 0 4px, 4px -4px, -4px 0 !important;
    }
    .asset-row { display: flex; gap: 12px; align-items: center; margin-bottom: 12px; }
    .asset-preview { max-height: 48px; max-width: 160px; object-fit: contain; border: 1px solid #e2e8f0; border-radius: 6px; padding: 4px; }
    .asset-preview--icon { max-height: 32px; max-width: 32px; }
    .source-hint { margin-top: 8px; color: #64748b; font-size: 0.875rem; }
  `]
})
export class StorefrontStoreManagerComponent implements OnInit, OnDestroy {
  private readonly service = inject(CatalogService);
  @ViewChild('profileEditor') profileEditor?: StorefrontProfileEditorComponent;
  @ViewChild('tenantProfileEditor') tenantProfileEditor?: StorefrontProfileEditorComponent;
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly currentContext = inject(CurrentContextService);
  readonly liveUpdateService = inject(LiveUpdateService);
  private readonly localization = inject(LocalizationService);

  private liveSubscription: MarketplaceLiveSubscription = { topic: null, eventsSubscription: null };

  readonly loading = signal(true);
  readonly acting = signal(false);
  readonly info = signal('');
  readonly rows = signal<MarketplaceStore[]>([]);
  readonly selectedRows = signal<MarketplaceStore[]>([]);
  readonly search = signal('');
  readonly sortField = signal<StoreSortField>('storeKey');
  readonly sortOrder = signal<1 | -1>(1);
  readonly domains = signal<StoreDomainBinding[]>([]);
  readonly selectedStoreId = signal<string | null>(null);
  readonly brandingSource = signal('system');
  readonly profileSource = signal('system');
  readonly tenantProfileSource = signal('system');
  readonly logoPreviewUrl = signal<string | null>(null);
  readonly faviconPreviewUrl = signal<string | null>(null);

  readonly featureFlagFields: StructuredJsonFieldDef[] = [
    { key: 'PackageMode', type: 'boolean', labelKey: 'catalog.storefront.stores.packageMode' },
    { key: 'MarketplaceMode', type: 'boolean', labelKey: 'catalog.storefront.stores.marketplaceMode' },
    {
      key: 'HeroLayout',
      type: 'select',
      labelKey: 'catalog.storefront.stores.heroLayout',
      options: [
        { value: 'split', labelKey: 'catalog.storefront.stores.heroLayoutOptions.split' },
        { value: 'banner', labelKey: 'catalog.storefront.stores.heroLayoutOptions.banner' }
      ]
    },
    {
      key: 'HeroTransition',
      type: 'select',
      labelKey: 'catalog.storefront.stores.heroTransition',
      options: [
        { value: 'fade', labelKey: 'catalog.storefront.stores.heroTransitionOptions.fade' },
        { value: 'slide', labelKey: 'catalog.storefront.stores.heroTransitionOptions.slide' }
      ]
    },
    {
      key: 'CategoryCardStyle',
      type: 'select',
      labelKey: 'catalog.storefront.stores.categoryCardStyle',
      options: [
        { value: 'icon', labelKey: 'catalog.storefront.stores.categoryCardStyleOptions.icon' },
        { value: 'banner', labelKey: 'catalog.storefront.stores.categoryCardStyleOptions.banner' }
      ]
    },
    ...STOREFRONT_NAV_ITEM_KEYS.map((item): StructuredJsonFieldDef => ({
      key: `Nav${item}`,
      type: 'boolean',
      labelKey: `catalog.storefront.stores.navItems.${item}`
    }))
  ];
  liveStatusKey = liveStatusKey;

  storeDialogVisible = false;
  settingsDialogVisible = false;
  domainDialogVisible = false;
  brandingDialogVisible = false;
  profileDialogVisible = false;
  tenantProfileDialogVisible = false;
  profileDraft: StorefrontContentProfile | null = null;
  tenantProfileDraft: StorefrontContentProfile | null = null;
  storeKey = '';
  storeName = '';
  defaultCulture = 'fa-IR';
  defaultCurrency = 'IRR';
  featureFlagsJson = '{}';
  private featureFlagsBaseJson = '{}';
  private settingsStore: MarketplaceStore | null = null;
  host = '';
  pathBase = '/';
  readonly brandingColorFields = BRANDING_COLOR_FIELDS;
  brandingColors: Record<BrandingColorField, string> = {
    primaryColor: '',
    secondaryColor: '',
    accentColor: '',
    storefrontLinkColor: '',
    backgroundColor: '',
    cardColor: '',
    borderColor: '',
    mutedTextColor: '',
    surfaceSoftColor: '',
    highlightColor: ''
  };
  logoFileId: string | null = null;
  faviconFileId: string | null = null;
  private logoUploadFile: File | null = null;
  private faviconUploadFile: File | null = null;

  ngOnInit(): void {
    this.search.set(this.route.snapshot.queryParamMap.get('q') ?? '');
    this.load();
    void this.startLiveUpdates();
  }

  ngOnDestroy(): void {
    void unsubscribeMarketplaceOperationsLiveUpdates(this.liveUpdateService, this.liveSubscription);
  }

  load(): void {
    this.loading.set(true);
    this.service.listMarketplaceStores().subscribe({
      next: rows => {
        this.rows.set(rows);
        this.info.set('');
        this.loading.set(false);
      },
      error: () => {
        this.rows.set([]);
        this.loading.set(false);
      }
    });
  }

  filteredRows(): MarketplaceStore[] {
    const value = this.search().trim().toLowerCase();
    const filtered = !value
      ? this.rows()
      : this.rows().filter(row =>
        row.storeKey.toLowerCase().includes(value)
        || row.name.toLowerCase().includes(value)
        || row.status.toLowerCase().includes(value)
        || row.defaultCulture.toLowerCase().includes(value)
        || row.defaultCurrency.toLowerCase().includes(value));

    const field = this.sortField();
    const order = this.sortOrder();
    return [...filtered].sort((left, right) => compareSortValues(left[field], right[field]) * order);
  }

  onSearch(event: Event): void {
    this.search.set((event.target as HTMLInputElement).value ?? '');
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { q: this.search().trim() || null },
      queryParamsHandling: 'merge'
    });
  }

  onSelectionChange(selection: MarketplaceStore[] | MarketplaceStore | null): void {
    this.selectedRows.set(Array.isArray(selection) ? selection : selection ? [selection] : []);
  }

  sortBy(field: StoreSortField): void {
    toggleSortField(
      this.sortField(),
      field,
      this.sortOrder(),
      value => this.sortField.set(value),
      value => this.sortOrder.set(value)
    );
  }

  openSelected(): void {
    const row = this.selectedRows()[0];
    if (row) {
      this.showDomains(row);
    }
  }

  applyBulk(): void {
    if (this.selectedRows().length === 0) {
      this.info.set(this.t('catalog.marketplace.actions.bulkNothingSelected'));
      return;
    }
    this.info.set(this.t('catalog.marketplace.actions.bulkQueued'));
  }

  exportRows(): void {
    const rows = this.filteredRows();
    if (rows.length === 0) {
      this.info.set(this.t('catalog.marketplace.actions.exportEmpty'));
      return;
    }

    downloadCsv(
      'storefront-stores.csv',
      ['StoreKey', 'Name', 'Status', 'DefaultCulture', 'DefaultCurrency'],
      rows.map(row => [row.storeKey, row.name, row.status, row.defaultCulture, row.defaultCurrency]));
    this.info.set(this.t('catalog.marketplace.actions.exportReady'));
  }

  openCreate(): void {
    this.storeKey = '';
    this.storeName = '';
    this.defaultCulture = 'fa-IR';
    this.defaultCurrency = 'IRR';
    this.featureFlagsBaseJson = '{}';
    this.featureFlagsJson = '{}';
    this.storeDialogVisible = true;
  }

  onFeatureFlagsJsonChange(nextJson: string): void {
    this.featureFlagsJson = this.normalizeFeatureFlagsJson(
      mergeStructuredJsonPayload(
        this.featureFlagFields,
        this.featureFlagsBaseJson,
        structuredJsonItemFromPayload(this.featureFlagFields, nextJson)
      )
    );
  }

  private normalizeFeatureFlagsJson(json: string): string {
    const parsed = parseStructuredJsonPayload(json);
    parsed['PackageMode'] = readStructuredJsonBoolean(json, 'PackageMode', ['singlePackageMode']);
    if (parsed['PackageMode'] !== true) {
      parsed['singlePackageMode'] = false;
    }
    // Default MarketplaceMode to true when absent so existing marketplace stores keep seller buybox.
    parsed['MarketplaceMode'] = Object.prototype.hasOwnProperty.call(parsed, 'MarketplaceMode')
      ? readStructuredJsonBoolean(json, 'MarketplaceMode')
      : true;
    // Absent nav keys stay visible on the storefront.
    for (const item of STOREFRONT_NAV_ITEM_KEYS) {
      const key = `Nav${item}`;
      parsed[key] = Object.prototype.hasOwnProperty.call(parsed, key)
        ? readStructuredJsonBoolean(json, key)
        : true;
    }
    return JSON.stringify(parsed);
  }

  private buildFeatureFlagsJson(): string {
    return this.normalizeFeatureFlagsJson(
      mergeStructuredJsonPayload(
        this.featureFlagFields,
        this.featureFlagsBaseJson,
        structuredJsonItemFromPayload(this.featureFlagFields, this.featureFlagsJson)
      )
    );
  }

  saveStore(): void {
    if (!this.storeKey.trim() || !this.storeName.trim()) return;
    this.acting.set(true);
    this.service.createMarketplaceStore({
      storeKey: this.storeKey.trim(),
      name: this.storeName.trim(),
      defaultCulture: this.defaultCulture?.trim() || 'fa-IR',
      defaultCurrency: this.defaultCurrency?.trim() || 'IRR',
      featureFlagsJson: this.buildFeatureFlagsJson()
    }).subscribe({
      next: () => {
        this.storeDialogVisible = false;
        this.load();
        this.acting.set(false);
      },
      error: () => this.acting.set(false)
    });
  }

  showSettings(store: MarketplaceStore): void {
    this.selectedStoreId.set(store.id);
    this.settingsStore = store;
    this.featureFlagsBaseJson = store.featureFlagsJson?.trim() || '{}';
    this.featureFlagsJson = this.normalizeFeatureFlagsJson(this.featureFlagsBaseJson);
    this.settingsDialogVisible = true;
  }

  saveSettings(): void {
    const store = this.settingsStore;
    if (!store) return;

    this.acting.set(true);
    this.service.updateMarketplaceStore(store.id, {
      storeKey: store.storeKey,
      name: store.name,
      defaultCulture: store.defaultCulture,
      defaultCurrency: store.defaultCurrency,
      status: store.status,
      featureFlagsJson: this.buildFeatureFlagsJson()
    }).subscribe({
      next: () => {
        this.settingsDialogVisible = false;
        this.settingsStore = null;
        this.load();
        this.acting.set(false);
        this.info.set(this.t('catalog.storefront.stores.settingsSaved'));
      },
      error: () => this.acting.set(false)
    });
  }

  showDomains(store: MarketplaceStore): void {
    this.selectedStoreId.set(store.id);
    this.host = '';
    this.pathBase = '/';
    this.domainDialogVisible = true;
    this.service.listStoreDomains(store.id).subscribe({
      next: rows => this.domains.set(rows),
      error: () => this.domains.set([])
    });
  }

  saveDomain(): void {
    const storeId = this.selectedStoreId();
    if (!storeId || !this.host.trim()) return;
    this.acting.set(true);
    this.service.upsertStoreDomain(storeId, {
      host: this.host.trim(),
      pathBase: this.pathBase.trim() || '/',
      isPrimary: this.domains().length === 0
    }).subscribe({
      next: () => {
        this.service.listStoreDomains(storeId).subscribe({
          next: rows => this.domains.set(rows),
          error: () => this.domains.set([])
        });
        this.acting.set(false);
      },
      error: () => this.acting.set(false)
    });
  }

  showBranding(store: MarketplaceStore): void {
    this.selectedStoreId.set(store.id);
    this.brandingColors = {
      primaryColor: '',
      secondaryColor: '',
      accentColor: '',
      storefrontLinkColor: '',
      backgroundColor: '',
      cardColor: '',
      borderColor: '',
      mutedTextColor: '',
      surfaceSoftColor: '',
      highlightColor: ''
    };
    this.logoFileId = null;
    this.faviconFileId = null;
    this.logoUploadFile = null;
    this.faviconUploadFile = null;
    this.logoPreviewUrl.set(null);
    this.faviconPreviewUrl.set(null);
    this.brandingDialogVisible = true;
    this.service.getStoreBranding(store.id).subscribe({
      next: branding => this.applyBrandingState(branding),
      error: () => this.brandingSource.set('system')
    });
  }

  showProfile(store: MarketplaceStore): void {
    this.selectedStoreId.set(store.id);
    this.profileDraft = null;
    this.profileDialogVisible = true;
    this.service.getStoreProfile(store.id).subscribe({
      next: response => {
        this.profileSource.set(response.source);
        queueMicrotask(() => this.profileEditor?.applyProfile(response.profile, response.source));
      },
      error: () => this.profileSource.set('system')
    });
  }

  onProfileDraftChange(profile: StorefrontContentProfile): void {
    this.profileDraft = profile;
  }

  saveProfile(): void {
    const storeId = this.selectedStoreId();
    if (!storeId) return;
    const profile = this.profileEditor?.buildProfile() ?? this.profileDraft;
    if (!profile) return;

    this.acting.set(true);
    this.service.updateStoreProfile(storeId, profile).subscribe({
      next: response => {
        this.profileSource.set(response.source);
        this.profileEditor?.applyProfile(response.profile, response.source);
        this.profileDialogVisible = false;
        this.acting.set(false);
        this.info.set(this.t('catalog.storefront.profile.saved'));
      },
      error: () => this.acting.set(false)
    });
  }

  showTenantDefaultProfile(): void {
    this.tenantProfileDraft = null;
    this.tenantProfileDialogVisible = true;
    this.service.getTenantDefaultProfile().subscribe({
      next: response => {
        this.tenantProfileSource.set(response.source);
        queueMicrotask(() => this.tenantProfileEditor?.applyProfile(response.profile, response.source));
      },
      error: () => this.tenantProfileSource.set('system')
    });
  }

  onTenantProfileDraftChange(profile: StorefrontContentProfile): void {
    this.tenantProfileDraft = profile;
  }

  saveTenantDefaultProfile(): void {
    const profile = this.tenantProfileEditor?.buildProfile() ?? this.tenantProfileDraft;
    if (!profile) return;

    this.acting.set(true);
    this.service.updateTenantDefaultProfile(profile).subscribe({
      next: response => {
        this.tenantProfileSource.set(response.source);
        this.tenantProfileEditor?.applyProfile(response.profile, response.source);
        this.tenantProfileDialogVisible = false;
        this.acting.set(false);
        this.info.set(this.t('catalog.storefront.profile.tenantDefault.saved'));
      },
      error: () => this.acting.set(false)
    });
  }

  onLogoSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] ?? null;
    this.logoUploadFile = file;
    if (file) {
      this.logoPreviewUrl.set(URL.createObjectURL(file));
    }
  }

  onFaviconSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] ?? null;
    this.faviconUploadFile = file;
    if (file) {
      this.faviconPreviewUrl.set(URL.createObjectURL(file));
    }
  }

  saveBranding(): void {
    const storeId = this.selectedStoreId();
    if (!storeId) return;

    this.acting.set(true);
    const uploads: Array<Promise<string | null>> = [];
    if (this.logoUploadFile) {
      uploads.push(new Promise(resolve => {
        this.service.uploadStoreBrandingAsset(storeId, this.logoUploadFile!).subscribe({
          next: result => resolve(result.fileId),
          error: () => resolve(null)
        });
      }));
    } else {
      uploads.push(Promise.resolve(this.logoFileId));
    }

    if (this.faviconUploadFile) {
      uploads.push(new Promise(resolve => {
        this.service.uploadStoreBrandingAsset(storeId, this.faviconUploadFile!).subscribe({
          next: result => resolve(result.fileId),
          error: () => resolve(null)
        });
      }));
    } else {
      uploads.push(Promise.resolve(this.faviconFileId));
    }

    void Promise.all(uploads).then(([logoId, faviconId]) => {
      this.service.updateStoreBranding(storeId, {
        primaryColor: this.normalizeColor(this.brandingColors.primaryColor),
        secondaryColor: this.normalizeColor(this.brandingColors.secondaryColor),
        accentColor: this.normalizeColor(this.brandingColors.accentColor),
        storefrontLinkColor: this.normalizeColor(this.brandingColors.storefrontLinkColor),
        backgroundColor: this.normalizeColor(this.brandingColors.backgroundColor),
        cardColor: this.normalizeColor(this.brandingColors.cardColor),
        borderColor: this.normalizeColor(this.brandingColors.borderColor),
        mutedTextColor: this.normalizeColor(this.brandingColors.mutedTextColor),
        surfaceSoftColor: this.normalizeColor(this.brandingColors.surfaceSoftColor),
        highlightColor: this.normalizeColor(this.brandingColors.highlightColor),
        logoFileId: logoId,
        faviconFileId: faviconId
      }).subscribe({
        next: branding => {
          this.applyBrandingState(branding);
          this.brandingDialogVisible = false;
          this.acting.set(false);
          this.info.set(this.t('catalog.storefront.branding.saved'));
        },
        error: () => this.acting.set(false)
      });
    });
  }

  onColorPickerChange(field: BrandingColorField, value: string | null | undefined): void {
    this.brandingColors[field] = this.normalizeHexColor(value ?? '') ?? '';
  }

  onColorHexChange(field: BrandingColorField, value: string): void {
    const trimmed = value.trim();
    if (!trimmed) {
      this.brandingColors[field] = '';
      return;
    }

    const normalized = this.normalizeHexColor(trimmed);
    this.brandingColors[field] = normalized ?? trimmed;
  }

  clearBrandingColor(field: BrandingColorField): void {
    this.brandingColors[field] = '';
  }

  toPickerHex(value: string): string | undefined {
    return this.normalizeHexColor(value) ?? undefined;
  }

  private applyBrandingState(branding: StoreBranding): void {
    const storeId = this.selectedStoreId();
    this.brandingColors = {
      primaryColor: this.normalizeHexColor(branding.primaryColor ?? '') ?? '',
      secondaryColor: this.normalizeHexColor(branding.secondaryColor ?? '') ?? '',
      accentColor: this.normalizeHexColor(branding.accentColor ?? '') ?? '',
      storefrontLinkColor: this.normalizeHexColor(branding.storefrontLinkColor ?? '') ?? '',
      backgroundColor: this.normalizeHexColor(branding.backgroundColor ?? '') ?? '',
      cardColor: this.normalizeHexColor(branding.cardColor ?? '') ?? '',
      borderColor: this.normalizeHexColor(branding.borderColor ?? '') ?? '',
      mutedTextColor: this.normalizeHexColor(branding.mutedTextColor ?? '') ?? '',
      surfaceSoftColor: this.normalizeHexColor(branding.surfaceSoftColor ?? '') ?? '',
      highlightColor: this.normalizeHexColor(branding.highlightColor ?? '') ?? ''
    };
    this.logoFileId = branding.logoFileId;
    this.faviconFileId = branding.faviconFileId;
    this.brandingSource.set(branding.source);
    this.logoPreviewUrl.set(storeId ? this.service.brandingFilePreviewUrl(storeId, branding.logoFileId) : null);
    this.faviconPreviewUrl.set(storeId ? this.service.brandingFilePreviewUrl(storeId, branding.faviconFileId) : null);
  }

  private normalizeColor(value: string): string | null {
    return this.normalizeHexColor(value.trim());
  }

  private normalizeHexColor(value: string): string | null {
    const trimmed = value.trim();
    if (!trimmed) {
      return null;
    }

    const withHash = trimmed.startsWith('#') ? trimmed : `#${trimmed}`;
    if (!/^#[0-9A-Fa-f]{6}$/.test(withHash)) {
      return null;
    }

    return withHash.toUpperCase();
  }

  private async startLiveUpdates(): Promise<void> {
    const tenantId = this.currentContext.context()?.tenantId;
    this.liveSubscription = await subscribeMarketplaceOperationsLiveUpdates(
      tenantId,
      this.liveUpdateService,
      () => this.load());
  }

  t(key: string): string { return this.localization.translate(key); }
}
